/* IBE-914 — shared slider for the Virtual Awards 2026 landing page.
   Built for T3 (Contest Rules) and reused unchanged by T5 (five category
   sliders on the same page), so nothing here knows what it is sliding.

   Contract — one root per slider, children found by data attribute:

     [data-lp-va-slider]     the root. Optional data-slider-media="<query>"
                             keeps the instance inert unless the query
                             matches; omit it for an always-on slider.
     [data-slider-track]     the scroll container. Required.
     [data-slider-slide]     the slides, inside the track. Required.
     [data-slider-counter]   optional. Text is set to "<n> / <total>".
     [data-slider-fill]      optional. Its width is set as a percentage.
     [data-slider-prev]      optional button. T5 supplies these.
     [data-slider-next]      optional button.

   The root gets `lp-va-slider--static` whenever the track does not overflow,
   which is how T5 shows its desktop arrows on overflow only.

   Scrolling is native with CSS scroll-snap; this file never transforms the
   track. It only measures, and programmatic scrolls respect
   prefers-reduced-motion. No dependencies. */

(function () {
  'use strict';

  /* Two sections on this page load this file, so it is evaluated twice. The
     first evaluation initialises the whole document, and initAll is
     idempotent, so the second is a no-op. */
  if (window.LpVaSlider) {
    return;
  }

  var instances = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function LpVaSlider(root) {
    this.root = root;
    this.track = root.querySelector('[data-slider-track]');
    this.slides = this.track
      ? Array.prototype.slice.call(this.track.querySelectorAll('[data-slider-slide]'))
      : [];
    this.counter = root.querySelector('[data-slider-counter]');
    this.fill = root.querySelector('[data-slider-fill]');
    this.prev = root.querySelector('[data-slider-prev]');
    this.next = root.querySelector('[data-slider-next]');

    this.active = false;
    this.frame = 0;

    var query = root.getAttribute('data-slider-media');
    this.media = query ? window.matchMedia(query) : null;

    this.onScroll = this.schedule.bind(this);
    this.onResize = this.schedule.bind(this);
    this.onMediaChange = this.sync.bind(this);
    this.onPrev = this.step.bind(this, -1);
    this.onNext = this.step.bind(this, 1);
  }

  LpVaSlider.prototype.usable = function () {
    return Boolean(this.track) && this.slides.length > 0;
  };

  LpVaSlider.prototype.connect = function () {
    if (!this.usable()) {
      return;
    }
    if (this.media) {
      /* Safari below 14 only has the deprecated addListener. */
      if (this.media.addEventListener) {
        this.media.addEventListener('change', this.onMediaChange);
      } else if (this.media.addListener) {
        this.media.addListener(this.onMediaChange);
      }
    }
    this.sync();
  };

  /* Turn the instance on or off to match data-slider-media. Contest Rules is
     a static grid above 750, so above 750 it must be genuinely inert — no
     listeners, and no stray tab stop on an element that no longer scrolls. */
  LpVaSlider.prototype.sync = function () {
    var shouldBeActive = this.media ? this.media.matches : true;
    if (shouldBeActive === this.active) {
      return;
    }
    if (shouldBeActive) {
      this.activate();
    } else {
      this.deactivate();
    }
  };

  LpVaSlider.prototype.activate = function () {
    this.active = true;

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.onResize);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    if (this.prev) {
      this.prev.addEventListener('click', this.onPrev);
    }
    if (this.next) {
      this.next.addEventListener('click', this.onNext);
    }

    /* A scroll container is not reliably focusable on its own, and without a
       tab stop a keyboard user cannot reach the slides that are off screen. */
    this.track.setAttribute('tabindex', '0');

    this.update();
  };

  LpVaSlider.prototype.deactivate = function () {
    this.active = false;

    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    this.track.removeEventListener('scroll', this.onScroll);

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    } else {
      window.removeEventListener('resize', this.onResize);
    }

    if (this.prev) {
      this.prev.removeEventListener('click', this.onPrev);
    }
    if (this.next) {
      this.next.removeEventListener('click', this.onNext);
    }

    this.track.removeAttribute('tabindex');
    this.root.classList.remove('lp-va-slider--static');

    if (this.fill) {
      this.fill.style.width = '';
    }
  };

  LpVaSlider.prototype.destroy = function () {
    if (!this.usable()) {
      return;
    }
    if (this.active) {
      this.deactivate();
    }
    if (this.media) {
      if (this.media.removeEventListener) {
        this.media.removeEventListener('change', this.onMediaChange);
      } else if (this.media.removeListener) {
        this.media.removeListener(this.onMediaChange);
      }
    }
  };

  /* scroll fires far more often than the chrome needs redrawing. */
  LpVaSlider.prototype.schedule = function () {
    if (this.frame) {
      return;
    }
    var self = this;
    this.frame = window.requestAnimationFrame(function () {
      self.frame = 0;
      self.update();
    });
  };

  /* Where a snapped slide's left edge lands, in viewport coordinates. Read
     from rects rather than offsetLeft so it does not depend on which
     ancestor happens to be the offsetParent. */
  LpVaSlider.prototype.origin = function () {
    var rect = this.track.getBoundingClientRect();
    var pad = parseFloat(window.getComputedStyle(this.track).paddingLeft);
    return rect.left + (isNaN(pad) ? 0 : pad);
  };

  LpVaSlider.prototype.currentIndex = function () {
    var origin = this.origin();
    var index = 0;
    var smallest = Infinity;

    for (var i = 0; i < this.slides.length; i += 1) {
      var distance = Math.abs(this.slides[i].getBoundingClientRect().left - origin);
      if (distance < smallest) {
        smallest = distance;
        index = i;
      }
    }
    return index;
  };

  LpVaSlider.prototype.update = function () {
    var track = this.track;
    var visible = track.clientWidth;
    var total = track.scrollWidth;
    var maxScroll = total - visible;
    var scrolled = track.scrollLeft;

    this.root.classList.toggle('lp-va-slider--static', maxScroll <= 1);

    if (this.counter) {
      this.counter.textContent = this.currentIndex() + 1 + ' / ' + this.slides.length;
    }

    if (this.fill) {
      /* Continuous rather than stepped: width tracks the scroll position
         itself, so it moves under a drag and reaches exactly 100% at the
         end of the track. The stepped alternative is
         (index + 1) / count. Set as a percentage of the bar, so adding or
         removing a card in the customizer stays correct — the drawn 114.117
         is not reproducible from any model and was eyeballed. */
      var progress = total > 0 ? ((scrolled + visible) / total) * 100 : 0;
      this.fill.style.width = Math.max(0, Math.min(100, progress)) + '%';
    }

    this.setDisabled(this.prev, scrolled <= 1);
    this.setDisabled(this.next, scrolled >= maxScroll - 1);
  };

  LpVaSlider.prototype.setDisabled = function (button, disabled) {
    if (!button) {
      return;
    }
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
  };

  LpVaSlider.prototype.step = function (direction, event) {
    if (event) {
      event.preventDefault();
    }
    var target = this.slides[
      Math.max(0, Math.min(this.slides.length - 1, this.currentIndex() + direction))
    ];
    if (!target) {
      return;
    }
    this.track.scrollBy({
      left: target.getBoundingClientRect().left - this.origin(),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  function initAll(root) {
    if (!root || !root.querySelectorAll) {
      return;
    }
    var roots = Array.prototype.slice.call(root.querySelectorAll('[data-lp-va-slider]'));
    if (root.matches && root.matches('[data-lp-va-slider]')) {
      roots.unshift(root);
    }
    roots.forEach(function (element) {
      if (instances.has(element)) {
        return;
      }
      var slider = new LpVaSlider(element);
      instances.set(element, slider);
      slider.connect();
    });
  }

  function destroyAll(root) {
    if (!root || !root.querySelectorAll) {
      return;
    }
    var roots = Array.prototype.slice.call(root.querySelectorAll('[data-lp-va-slider]'));
    if (root.matches && root.matches('[data-lp-va-slider]')) {
      roots.unshift(root);
    }
    roots.forEach(function (element) {
      var slider = instances.get(element);
      if (slider) {
        slider.destroy();
        instances.delete(element);
      }
    });
  }

  window.LpVaSlider = LpVaSlider;

  /* The tag is deferred, so the document is parsed by the time this runs.
     The readyState guard is belt and braces for a future non-deferred load. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Without these the customizer breaks: editing a section replaces its DOM,
     which orphans the old instance and leaves the new markup unbound. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target);
  });
})();
